/**
 * SlidePanel — secondary panel that slides in alongside the DAG.
 *
 * Tabs are draggable: drag one onto the main area to promote it.
 * Options menu contains layout preferences (sidebar side).
 *
 * Each tab is mounted lazily on first activation and kept alive.
 * When a tab is promoted to the main pane, its content is NOT
 * rendered here (ProjectLayout renders it). A "DAG" tab appears
 * in the bar so the user can view the DAG in the sidebar.
 */

import { useCallback, useMemo, useRef, useState, type DragEvent } from "react";
import { Flex, Text } from "@radix-ui/themes";

import { useProvenanceContext, type PanelTab } from "../../context/ProvenanceContext";
import { DRAG_TAB_TYPE } from "./panelDrag";
import DAGPanel from "../panels/DAGPanel";
import DataExplorerPanel from "../panels/DataExplorerPanel";
import PlotBuilderPanel from "../panels/PlotBuilderPanel";
import SavedPlotsPanel from "../panels/SavedPlotsPanel";
import ArtifactDetailPanel from "../panels/ArtifactDetailPanel";
import Flow360ViewerPanel from "../panels/Flow360ViewerPanel";
import ReproducibilityPanel from "../panels/ReproducibilityPanel";
import ConfigurationPanel from "../panels/ConfigurationPanel";
import ConversationsPanel from "../panels/ConversationsPanel";
import ForkPanel from "../../flow360/ForkPanel";
import { VersionHistoryPanel } from "../versioning/VersionHistoryPanel";
import GeometryPanel from "../panels/GeometryPanel";
import CampaignPanel from "../panels/CampaignPanel";
import { createPlot, type PlotScope } from "../../api/plots";
import type { PlotBuilderConfig } from "../plot-builder/types";
import { createDefaultConfig } from "../plot-builder/types";
import { useReferenceQuantities } from "../../hooks/useReferenceQuantities";
import { extractWingArea } from "../../hooks/usePlotComparison";

interface Props {
  projectId: string;
  promotedTab?: PanelTab;
  onDemote?: () => void;
  sidebarLeft?: boolean;
  onToggleSidebarSide?: () => void;
  isMaximized?: boolean;
  onToggleMaximize?: () => void;
}

const BASE_TABS: { id: PanelTab & string; label: string }[] = [
  { id: "data", label: "Data" },
  { id: "plot", label: "Plot Builder" },
  { id: "saved-plots", label: "Saved Plots" },
  { id: "detail", label: "Detail" },
  { id: "viewer", label: "Viewer" },
  { id: "cockpit", label: "Cockpit" },
  { id: "history", label: "History" },
  { id: "config", label: "Config" },
  { id: "repro", label: "Repro" },
  { id: "conversations", label: "Conversations" },
  { id: "geometry", label: "Geometry" },
  { id: "campaign", label: "Adaptive Sampling" },
];

const DAG_TAB = { id: "dag" as const, label: "DAG" };

const btnBase: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 5,
  padding: "6px 10px", fontSize: 11, borderRadius: 5,
  cursor: "pointer", lineHeight: 1, fontFamily: "inherit",
};

export function SlidePanel({ projectId, promotedTab, sidebarLeft, onToggleSidebarSide, isMaximized, onToggleMaximize }: Props) {
  const { openPanel, setOpenPanel, plotBuilderSeed, currentBranch } = useProvenanceContext();
  const MAX_KEPT_PANELS = 3;
  const mounted = useRef<Set<string>>(new Set());
  const mountOrder = useRef<string[]>([]);
  if (openPanel) {
    // Move to end of order (most recently used)
    mountOrder.current = mountOrder.current.filter(p => p !== openPanel);
    mountOrder.current.push(openPanel);
    mounted.current.add(openPanel);
    // Evict panels beyond the limit
    while (mountOrder.current.length > MAX_KEPT_PANELS) {
      const evicted = mountOrder.current.shift()!;
      if (evicted !== openPanel) mounted.current.delete(evicted);
    }
  }
  const [optionsOpen, setOptionsOpen] = useState(false);
  const optionsRef = useRef<HTMLDivElement>(null);

  const { data: refQuantities } = useReferenceQuantities(projectId, currentBranch);
  const [loadedPlotConfig, setLoadedPlotConfig] = useState<PlotBuilderConfig | undefined>();

  const seededConfig = useMemo(() => {
    if (loadedPlotConfig) return loadedPlotConfig;
    if (!plotBuilderSeed) return undefined;
    const cfg = createDefaultConfig();
    if (plotBuilderSeed.zField) {
      cfg.chartType = "scatter3d";
      cfg.engine = "plotly";
      cfg.zAxis = { field: plotBuilderSeed.zField, scale: "linear" };
      cfg.title = `${plotBuilderSeed.xField} vs ${plotBuilderSeed.yField} vs ${plotBuilderSeed.zField}`;
    } else {
      cfg.chartType = "scatter";
      cfg.title = `${plotBuilderSeed.xField} vs ${plotBuilderSeed.yField}`;
    }
    cfg.xAxis.field = plotBuilderSeed.xField;
    cfg.yAxis.field = plotBuilderSeed.yField;
    return cfg;
  }, [plotBuilderSeed, loadedPlotConfig]);

  const handleSavePlot = useCallback(
    async (config: PlotBuilderConfig, data: Record<string, unknown>[], thumbnail?: string, scope?: PlotScope) => {
      const sourceNodeIds = [...new Set(
        data.map((row) => row._node_id as string).filter(Boolean),
      )];
      const { value: wingArea, units: wingAreaUnits } = extractWingArea(refQuantities);
      const configWithSnapshot: Record<string, unknown> = {
        ...(config as unknown as Record<string, unknown>),
        _thumbnail: thumbnail,
        ...(wingArea != null && { snapshotRefArea: wingArea, snapshotRefAreaUnits: wingAreaUnits ?? "m²" }),
      };
      await createPlot({
        project_id: projectId,
        title: config.title || "Untitled Plot",
        chart_type: config.chartType,
        config: configWithSnapshot,
        data_snapshot: data,
        source_node_ids: sourceNodeIds,
        branch: currentBranch,
        scope: scope ?? (currentBranch === "main" ? "project" : "branch"),
      });
    },
    [projectId, currentBranch, refQuantities],
  );

  const handleLoadPlot = useCallback(
    (config: PlotBuilderConfig, _data: Record<string, unknown>[]) => {
      setLoadedPlotConfig(config);
      setOpenPanel("plot");
    },
    [setOpenPanel],
  );

  const handleTabDragStart = useCallback((e: DragEvent, tabId: string) => {
    e.dataTransfer.setData(DRAG_TAB_TYPE, tabId);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const handleOptionsBlur = useCallback(() => {
    setTimeout(() => {
      if (optionsRef.current && !optionsRef.current.contains(document.activeElement)) {
        setOptionsOpen(false);
      }
    }, 100);
  }, []);

  // Build tabs: include DAG tab when a panel is promoted to main
  const tabs = useMemo(() => {
    if (promotedTab) return [DAG_TAB, ...BASE_TABS];
    return BASE_TABS;
  }, [promotedTab]);

  const isPromoted = (tabId: string) => promotedTab === tabId;

  return (
    <div data-tour="slide-panel" style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Tab bar */}
      <Flex
        data-tour="tab-bar"
        align="center"
        style={{
          borderBottom: "1px solid var(--gray-a4)",
          background: "var(--color-background)",
          flexShrink: 0,
          overflowX: "auto",
        }}
      >
        {tabs.map((tab) => {
          const isActive = openPanel === tab.id;
          const promoted = isPromoted(tab.id);
          return (
            <button
              key={tab.id}
              draggable
              onDragStart={(e) => handleTabDragStart(e, tab.id)}
              onClick={() => setOpenPanel(tab.id)}
              style={{
                padding: "8px 12px",
                fontSize: 12,
                fontWeight: isActive ? 600 : 400,
                color: tab.id === "dag"
                  ? "var(--grass-11)"
                  : promoted
                    ? "var(--accent-11)"
                    : isActive
                      ? "var(--accent-11)"
                      : "var(--gray-11)",
                background: tab.id === "dag"
                  ? (isActive ? "var(--grass-a3)" : "transparent")
                  : promoted
                    ? "var(--accent-a3)"
                    : "transparent",
                border: "none",
                borderBottom: isActive ? `2px solid ${tab.id === "dag" ? "var(--grass-9)" : "var(--accent-9)"}` : "2px solid transparent",
                cursor: "grab",
                transition: "color 150ms, border-color 150ms",
                fontFamily: "inherit",
                whiteSpace: "nowrap",
              }}
              title={tab.id === "dag" ? "Click to view DAG, drag to main to restore" : "Click to view, drag to main area to promote"}
            >
              {tab.label}
              {promoted && <span style={{ marginLeft: 4, fontSize: 9, opacity: 0.6 }}>↗</span>}
            </button>
          );
        })}

        {/* Options + Close */}
        <div style={{ marginLeft: "auto", display: "flex", gap: 4, alignItems: "center", marginRight: 8, flexShrink: 0, position: "relative" }}>
          <div ref={optionsRef} onBlur={handleOptionsBlur} style={{ position: "relative" }}>
            <button
              onClick={() => setOptionsOpen(prev => !prev)}
              title="Panel options"
              style={{
                ...btnBase,
                color: "var(--gray-11)", background: "transparent",
                border: "1px solid var(--gray-a5)",
                padding: "5px 7px",
              }}
            >
              <svg width="12" height="12" viewBox="0 0 15 15" fill="none">
                <path d="M3.5 7.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Zm5 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0Z" fill="currentColor" />
              </svg>
            </button>

            {optionsOpen && (
              <div style={{
                position: "absolute", top: "100%", right: 0, marginTop: 4,
                background: "var(--color-surface, #1a1a2e)", border: "1px solid var(--gray-a5)",
                borderRadius: 6, padding: 4, minWidth: 160, zIndex: 20,
                boxShadow: "0 4px 16px rgba(0,0,0,0.3)",
              }}>
                <button
                  onClick={() => { onToggleSidebarSide?.(); setOptionsOpen(false); }}
                  style={{
                    display: "flex", alignItems: "center", gap: 8,
                    width: "100%", padding: "6px 10px", fontSize: 11,
                    color: "var(--gray-12)", background: "transparent",
                    border: "none", borderRadius: 4, cursor: "pointer",
                    fontFamily: "inherit", textAlign: "left",
                  }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--gray-a3)"; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "transparent"; }}
                >
                  <svg width="12" height="12" viewBox="0 0 15 15" fill="none">
                    <path d="M1.5 2a.5.5 0 0 0-.5.5v10a.5.5 0 0 0 .5.5h12a.5.5 0 0 0 .5-.5v-10a.5.5 0 0 0-.5-.5h-12ZM7 3v9" stroke="currentColor" strokeWidth="1" strokeLinecap="round" />
                  </svg>
                  Sidebar: {sidebarLeft ? "Left" : "Right"}
                </button>
              </div>
            )}
          </div>

          {onToggleMaximize && (
            <button
              onClick={onToggleMaximize}
              title={isMaximized ? "Restore panel" : "Maximize panel"}
              style={{
                ...btnBase,
                color: "var(--gray-11)", background: isMaximized ? "var(--accent-a3)" : "transparent",
                border: "1px solid var(--gray-a5)",
              }}
            >
              {isMaximized ? (
                <svg width="12" height="12" viewBox="0 0 15 15" fill="none">
                  <path d="M5.5 2a.5.5 0 0 0 0 1H6.3L4.15 5.15a.5.5 0 0 0 .7.7L7 3.71V4.5a.5.5 0 0 0 1 0v-2a.5.5 0 0 0-.5-.5h-2ZM4.85 9.85 3 11.71V10.5a.5.5 0 0 0-1 0v2.5h2.5a.5.5 0 0 0 0-1H3.29l2.15-2.15a.5.5 0 1 0-.7-.7l.11-.3ZM10.5 2a.5.5 0 0 1 0 1H9.71l2.15 2.15a.5.5 0 0 1-.7.7L9 3.71V4.5a.5.5 0 0 1-1 0v-2a.5.5 0 0 1 .5-.5h2ZM11.15 9.85 13 11.71V10.5a.5.5 0 0 1 1 0v2.5h-2.5a.5.5 0 0 1 0-1h1.21l-2.15-2.15a.5.5 0 0 1 .7-.7l-.11-.3Z" fill="currentColor"/>
                </svg>
              ) : (
                <svg width="12" height="12" viewBox="0 0 15 15" fill="none">
                  <path d="M2 2.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1H3v3.5a.5.5 0 0 1-1 0v-4ZM12.5 2a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-1 0V3H8.5a.5.5 0 0 1 0-1h4ZM2 12.5a.5.5 0 0 0 .5.5h4a.5.5 0 0 0 0-1H3V8.5a.5.5 0 0 0-1 0v4ZM12.5 13a.5.5 0 0 0 .5-.5v-4a.5.5 0 0 0-1 0V12H8.5a.5.5 0 0 0 0 1h4Z" fill="currentColor"/>
                </svg>
              )}
              {isMaximized ? "Restore" : "Maximize"}
            </button>
          )}

          <button
            onClick={() => { setOpenPanel(null); onToggleMaximize && isMaximized && onToggleMaximize(); }}
            title="Close panel"
            style={{
              ...btnBase,
              color: "var(--gray-11)", background: "transparent",
              border: "1px solid var(--gray-a5)",
            }}
          >
            <svg width="10" height="10" viewBox="0 0 15 15" fill="none">
              <path d="M11.78 4.22a.75.75 0 0 0-1.06 0L7.5 7.44 4.28 4.22a.75.75 0 0 0-1.06 1.06L6.44 8.5l-3.22 3.22a.75.75 0 1 0 1.06 1.06L7.5 9.56l3.22 3.22a.75.75 0 1 0 1.06-1.06L8.56 8.5l3.22-3.22a.75.75 0 0 0 0-1.06Z" fill="currentColor"/>
            </svg>
            Close
          </button>
        </div>
      </Flex>

      {/* Tab content — keep-alive; skip rendering the promoted tab (it lives in the main pane) */}
      <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
        {/* DAG in sidebar (only when a tab is promoted to main) */}
        {promotedTab && mounted.current.has("dag") && (
          <div style={{ display: openPanel === "dag" ? "flex" : "none", flexDirection: "column", height: "100%" }}>
            <DAGPanel projectId={projectId} />
          </div>
        )}

        {!isPromoted("data") && mounted.current.has("data") && (
          <div data-tour="panel-data" style={{ display: openPanel === "data" ? "flex" : "none", flexDirection: "column", height: "100%" }}>
            <DataExplorerPanel projectId={projectId} isVisible={openPanel === "data"} />
          </div>
        )}

        {!isPromoted("plot") && mounted.current.has("plot") && (
          <div data-tour="panel-plot" style={{ display: openPanel === "plot" ? "block" : "none", height: "100%" }}>
            <PlotBuilderPanel
              projectId={projectId}
              onSave={handleSavePlot}
              initialConfig={seededConfig}
            />
          </div>
        )}

        {!isPromoted("saved-plots") && mounted.current.has("saved-plots") && (
          <div style={{ display: openPanel === "saved-plots" ? "block" : "none", height: "100%" }}>
            <SavedPlotsPanel projectId={projectId} onLoadPlot={handleLoadPlot} />
          </div>
        )}

        {!isPromoted("detail") && mounted.current.has("detail") && (
          <div style={{ display: openPanel === "detail" ? "block" : "none", height: "100%" }}>
            <ArtifactDetailPanel />
          </div>
        )}

        {!isPromoted("viewer") && mounted.current.has("viewer") && (
          <div style={{ display: openPanel === "viewer" ? "block" : "none", height: "100%" }}>
            <Flow360ViewerPanel projectId={projectId} />
          </div>
        )}

        {!isPromoted("cockpit") && mounted.current.has("cockpit") && (
          <div style={{ display: openPanel === "cockpit" ? "block" : "none", height: "100%" }}>
            <ForkPanel projectId={projectId} />
          </div>
        )}

        {!isPromoted("history") && mounted.current.has("history") && (
          <div style={{ display: openPanel === "history" ? "block" : "none", height: "100%" }}>
            <VersionHistoryPanel projectId={projectId} />
          </div>
        )}

        {!isPromoted("config") && mounted.current.has("config") && (
          <div style={{ display: openPanel === "config" ? "block" : "none", height: "100%", overflow: "auto" }}>
            <div style={{ padding: 16 }}>
              <ConfigurationPanel projectId={projectId} />
            </div>
          </div>
        )}

        {!isPromoted("repro") && mounted.current.has("repro") && (
          <div style={{ display: openPanel === "repro" ? "block" : "none", height: "100%", overflow: "auto" }}>
            <div style={{ padding: 16 }}>
              <ReproducibilityPanel projectId={projectId} />
            </div>
          </div>
        )}

        {!isPromoted("conversations") && mounted.current.has("conversations") && (
          <div style={{ display: openPanel === "conversations" ? "block" : "none", height: "100%" }}>
            <ConversationsPanel />
          </div>
        )}

        {!isPromoted("geometry") && mounted.current.has("geometry") && (
          <div style={{ display: openPanel === "geometry" ? "block" : "none", height: "100%", overflow: "auto" }}>
            <div style={{ padding: 16 }}>
              <GeometryPanel projectId={projectId} />
            </div>
          </div>
        )}

        {!isPromoted("campaign") && mounted.current.has("campaign") && (
          <div style={{ display: openPanel === "campaign" ? "block" : "none", height: "100%" }}>
            <CampaignPanel projectId={projectId} />
          </div>
        )}

        {!openPanel && (
          <Flex align="center" justify="center" style={{ height: "100%" }}>
            <Text size="2" color="gray">Select a panel</Text>
          </Flex>
        )}
      </div>
    </div>
  );
}
